// AD-2790: the school's own Staff Categories, with sub levels one deep, as Admissions Categories.

let newRowCount = 0;

// A key for each row, so a new category can be chosen as the parent of another before it is saved.
export const rowKey = row => row.key ?? `saved-${row.id}`;
export const newRowKey = () => `new-${++newRowCount}`;

export const toRows = categories => {
    const ids = new Set(categories.map(c => c.id));

    return categories.map(c => ({
        id: c.id,
        key: `saved-${c.id}`,
        name: c.name,
        isSubLevel: Boolean(c.parentId && ids.has(c.parentId)),
        staffCount: c.staffCount ?? 0,
        formCount: c.formCount ?? 0,
        documentCount: c.documentCount ?? 0
    }));
};

// The first row can never be a sub level.
export const normaliseRows = rows => rows.map((row, index) => ({...row, isSubLevel: index === 0 ? false : row.isSubLevel}));

// A sub level belongs to the nearest category above it. A saved parent is sent by its id, a new one by its position.
export const toPayload = rows => {
    let lastCategoryPosition = null;

    return rows.map((row, index) => {
        const parentPosition = row.isSubLevel ? lastCategoryPosition : null;

        if (!row.isSubLevel) {
            lastCategoryPosition = index;
        }

        const parent = parentPosition !== null ? rows[parentPosition] : null;

        return {
            id: row.id,
            name: row.name.trim(),
            parentId: parent?.id ?? null,
            parentPosition: parent && parent.id ? null : parentPosition
        };
    });
};

// The sub levels that go with a category when it is removed.
export const subLevelsOf = (rows, index) => {
    if (rows[index]?.isSubLevel) {
        return [];
    }

    const result = [];

    for (let i = index + 1; i < rows.length && rows[i].isSubLevel; i++) {
        result.push(rows[i]);
    }

    return result;
};

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

// Before a category in use is removed, the School Admin is told how many staff profiles, forms and documents use it.
// The counts from the server already include a category's sub levels.
export const removalWarning = row => {
    const sentFrom = [
        row.formCount > 0 ? plural(row.formCount, 'form') : null,
        row.documentCount > 0 ? plural(row.documentCount, 'document') : null
    ].filter(Boolean).join(' and ');
    const uses = [
        row.staffCount > 0 ? `'${row.name}' is ticked on ${plural(row.staffCount, 'staff profile')}.` : null,
        sentFrom ? `Its group is used by ${sentFrom}.` : null
    ].filter(Boolean);

    if (uses.length === 0) {
        return null;
    }

    return `${uses.join(' ')} If you remove it, its group will be removed and it will be taken off every staff ` +
        'profile it is ticked on.';
};

export const listValidationError = rows => {
    const seen = new Set();
    let parentKey = '';

    for (const row of rows) {
        if (!row.name?.trim()) {
            return 'Every staff category needs a name.';
        }

        if (!row.isSubLevel) {
            parentKey = row.name.trim().toLowerCase();
        }

        const key = row.isSubLevel ? `${parentKey}/${row.name.trim().toLowerCase()}` : row.name.trim().toLowerCase();

        if (seen.has(key)) {
            return 'Two staff categories at the same level cannot have the same name.';
        }

        seen.add(key);
    }

    return '';
};

// Maria, 29 October 2026: Staff Categories are in alphabetical order, and the sub levels of each category in
// alphabetical order under it, until someone changes the order with the arrows or by dragging.
const byName = (a, b) => a.name.trim().localeCompare(b.name.trim(), 'en-GB', {sensitivity: 'base'});

// The list as groups: each category with its sub levels.
const toGroups = rows => rows.reduce((groups, row, index) => {
    if (row.isSubLevel && groups.length > 0 && index > 0) {
        groups[groups.length - 1].subLevels.push(row);
    } else {
        groups.push({category: {...row, isSubLevel: false}, subLevels: []});
    }
    return groups;
}, []);

const fromGroups = groups => groups.flatMap(g => [g.category, ...g.subLevels]);

export const sortAlphabetically = rows => fromGroups(
    toGroups(rows)
        .map(g => ({category: g.category, subLevels: [...g.subLevels].sort(byName)}))
        .sort((a, b) => byName(a.category, b.category))
);

export const isAlphabetical = rows =>
    sortAlphabetically(rows).map(rowKey).join('|') === normaliseRows(rows).map(rowKey).join('|');

// The categories a new category can be a sub level of: every category that is not itself a sub level.
export const parentChoices = rows => rows.filter(row => !row.isSubLevel).map(row => ({value: rowKey(row), title: row.name}));

// A new category goes at the end, or as the last sub level of the category chosen as its parent.
export const addRow = (rows, row, parentKey) => {
    const parentIndex = parentKey ? rows.findIndex(r => rowKey(r) === parentKey && !r.isSubLevel) : -1;

    if (parentIndex < 0) {
        return [...rows, {...row, isSubLevel: false}];
    }

    const insertAt = parentIndex + 1 + subLevelsOf(rows, parentIndex).length;

    return [...rows.slice(0, insertAt), {...row, isSubLevel: true}, ...rows.slice(insertAt)];
};

// The arrows move a category, with its sub levels, past the next category up or down, and a sub level past the next
// sub level of the same category. A sub level is moved to another category by dragging it. The list is returned
// unchanged when there is nothing to move past.
export const moveRow = (rows, index, step) => {
    const groups = toGroups(normaliseRows(rows));
    let group = 0;
    let start = 0;

    while (group < groups.length && start + 1 + groups[group].subLevels.length <= index) {
        start += 1 + groups[group].subLevels.length;
        group++;
    }

    if (group >= groups.length) {
        return rows;
    }

    const next = [...groups];

    if (index === start) {
        const target = group + step;

        if (target < 0 || target >= groups.length) {
            return rows;
        }

        [next[group], next[target]] = [next[target], next[group]];
        return normaliseRows(fromGroups(next));
    }

    const subLevels = [...groups[group].subLevels];
    const from = index - start - 1;
    const to = from + step;

    if (to < 0 || to >= subLevels.length) {
        return rows;
    }

    [subLevels[from], subLevels[to]] = [subLevels[to], subLevels[from]];
    next[group] = {...groups[group], subLevels};
    return normaliseRows(fromGroups(next));
};

// Dragging a category moves its sub levels with it, and it is never dropped between another category's sub
// levels. A sub level dragged on its own keeps belonging to the nearest category above where it is dropped.
export const dragRow = (rows, from, to) => {
    if (from === to || from < 0 || to < 0 || from >= rows.length || to >= rows.length) {
        return rows;
    }

    const moving = rows[from];

    if (moving.isSubLevel) {
        const next = [...rows];
        next.splice(from, 1);
        next.splice(to, 0, moving);
        return normaliseRows(next);
    }

    const block = [moving, ...subLevelsOf(rows, from)];
    const rest = [...rows.slice(0, from), ...rows.slice(from + block.length)];
    // where the dragged row lands in the list without its block
    let at = to > from ? to - block.length + 1 : to;
    at = Math.max(0, Math.min(at, rest.length));
    // never inside another category's sub levels
    while (at < rest.length && rest[at].isSubLevel) {
        at++;
    }

    return normaliseRows([...rest.slice(0, at), ...block, ...rest.slice(at)]);
};
